// Ganforge API layer — THE typed client for every derived-intelligence
// surface (unified from the former ganforge.ts + ganforge-intel.ts).
//
// Two transports, one module:
//
// 1. Forge hub (cross-origin, user-configured): the /forge/* pages point
//    at any Ganforge deployment via Settings (localStorage apiBase +
//    token). `rpc`/`callTool` throw typed ForgeError states.
// 2. Same-origin gateway (cookie-auth’d): the /repos/:owner/:repo/*
//    intelligence pages ride the app’s own gateway under /mcp and /v1
//    (vite proxy in dev). `mcpToolCall`/`probeHammer` never throw — they
//    return designed-state unions. The session bearer, when token auth
//    lands, is wired in exactly one place: lib/api.ts (read through
//    getBearer()).
//
// Both speak the protocol of `gan mcp` on a laptop: newline-framed
// JSON-RPC over stdio there, plain JSON-RPC bodies over HTTP here. One
// request shape, one response shape, one freshness contract
// (docs/19-mcp-surface.md in the ganvil repo):
//
//   POST {base}/mcp/{tenant}/{repo}
//   Authorization: Bearer <token>
//   { "jsonrpc": "2.0", "id": 1, "method": "tools/call",
//     "params": { "name": "repo_map", "arguments": {} } }
//
// Every tool answer carries a freshness envelope — captured_at, the head
// it reflects, and what it is good until (the next mutation of that
// state). This module passes that through untouched so the UI can stamp
// each panel with the state its numbers reflect.

import { getBearer, fetchWithRetry } from "./api";
import type { PrincipalUsageRow } from "./usageJoin";

// ---------------------------------------------------------------------------
// Connection settings (localStorage-backed)
// ---------------------------------------------------------------------------

const LS_BASE = "ganforge.apiBase";
const LS_TOKEN = "ganforge.token";
const LS_REPOS = "ganforge.repos";

export interface RepoRef {
  tenant: string;
  repo: string;
}

export interface ForgeSettings {
  apiBase: string;
  token: string;
}

export function loadSettings(): ForgeSettings {
  return {
    apiBase: localStorage.getItem(LS_BASE) ?? "",
    token: localStorage.getItem(LS_TOKEN) ?? "",
  };
}

export function saveSettings(s: ForgeSettings): void {
  localStorage.setItem(LS_BASE, s.apiBase.trim().replace(/\/+$/, ""));
  localStorage.setItem(LS_TOKEN, s.token.trim());
}

export function configured(): boolean {
  return loadSettings().apiBase.length > 0;
}

export const DEFAULT_REPOS: RepoRef[] = [
  { tenant: "ganvil", repo: "ganvil-cloud" },
  { tenant: "ganvil", repo: "ganvil" },
];

/** Tracked repositories. Defaults to primary active repositories, auto-populated from MCP & platform. */
export function loadRepos(): RepoRef[] {
  try {
    const raw = localStorage.getItem(LS_REPOS);
    if (!raw) {
      saveRepos(DEFAULT_REPOS);
      return DEFAULT_REPOS;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      saveRepos(DEFAULT_REPOS);
      return DEFAULT_REPOS;
    }
    const filtered = parsed.filter(
      (r): r is RepoRef =>
        typeof r === "object" &&
        r !== null &&
        typeof (r as RepoRef).tenant === "string" &&
        typeof (r as RepoRef).repo === "string",
    );
    if (filtered.length === 0) {
      saveRepos(DEFAULT_REPOS);
      return DEFAULT_REPOS;
    }
    return filtered;
  } catch {
    return DEFAULT_REPOS;
  }
}

/** Automatically discover repositories from backend releases, MCP sessions, and platform catalog. */
export async function discoverRepos(): Promise<RepoRef[]> {
  const current = loadRepos();
  const map = new Map<string, RepoRef>();
  for (const r of current) {
    map.set(`${r.tenant}/${r.repo}`, r);
  }
  for (const d of DEFAULT_REPOS) {
    map.set(`${d.tenant}/${d.repo}`, d);
  }

  // 1. Discover from published releases
  try {
    const res = await fetch("/api/v1/releases/recent");
    if (res.ok) {
      const releases = await res.json();
      if (Array.isArray(releases)) {
        for (const rel of releases) {
          const t = rel.repo_owner || "ganvil";
          const r = rel.repo_name || "ganvil-cloud";
          map.set(`${t}/${r}`, { tenant: t, repo: r });
        }
      }
    }
  } catch (_e) {}

  // 2. Discover from org repositories
  try {
    const res = await fetch("/api/v1/orgs/ganvil/repos");
    if (res.ok) {
      const orgRepos = await res.json();
      if (Array.isArray(orgRepos)) {
        for (const item of orgRepos) {
          const t = item.owner || "ganvil";
          const r = item.name;
          if (t && r) map.set(`${t}/${r}`, { tenant: t, repo: r });
        }
      }
    }
  } catch (_e) {}

  const merged = Array.from(map.values());
  saveRepos(merged);
  return merged;
}

export function saveRepos(repos: RepoRef[]): void {
  localStorage.setItem(LS_REPOS, JSON.stringify(repos));
}

export function addRepo(r: RepoRef): RepoRef[] {
  const list = loadRepos();
  if (!list.some((x) => x.tenant === r.tenant && x.repo === r.repo)) {
    list.push({ tenant: r.tenant.trim(), repo: r.repo.trim() });
    saveRepos(list);
  }
  return list;
}

export function removeRepo(r: RepoRef): RepoRef[] {
  const list = loadRepos().filter((x) => !(x.tenant === r.tenant && x.repo === r.repo));
  saveRepos(list);
  return list;
}

// ---------------------------------------------------------------------------
// JSON-RPC plumbing
// ---------------------------------------------------------------------------

export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

export class ForgeError extends Error {
  constructor(
    public kind: "unconfigured" | "unreachable" | "unauthorized" | "http" | "rpc" | "tool" | "shape",
    message: string,
    public status?: number,
  ) {
    super(message);
  }
}

/** Human-first message for anything thrown out of this module. */
export function forgeErrorMessage(e: unknown): string {
  if (e instanceof ForgeError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong talking to the index.";
}

interface RpcEnvelope {
  jsonrpc: "2.0";
  id: number;
  result?: Json;
  error?: { code: number; message: string };
}

let rpcId = 0;

/** One JSON-RPC call against a repo's hosted MCP endpoint. */
export async function rpc(ref: RepoRef, method: string, params?: Json): Promise<Json> {
  const { apiBase, token } = loadSettings();
  if (!apiBase) {
    throw new ForgeError(
      "unconfigured",
      "No API base configured — point Settings at your Ganforge deployment.",
    );
  }
  const url = `${apiBase}/mcp/${encodeURIComponent(ref.tenant)}/${encodeURIComponent(ref.repo)}`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetchWithRetry(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ jsonrpc: "2.0", id: ++rpcId, method, params: params ?? {} }),
    });
  } catch {
    throw new ForgeError(
      "unreachable",
      `Could not reach ${apiBase} — is the index host up and CORS-open to this origin?`,
    );
  }
  if (res.status === 401 || res.status === 403) {
    throw new ForgeError(
      "unauthorized",
      "The host rejected the bearer token. Check Settings → token.",
      res.status,
    );
  }
  if (!res.ok) {
    throw new ForgeError("http", `HTTP ${res.status} from the index host.`, res.status);
  }
  let body: RpcEnvelope;
  try {
    body = (await res.json()) as RpcEnvelope;
  } catch {
    throw new ForgeError("shape", "The host answered, but not with JSON-RPC.");
  }
  if (body.error) {
    throw new ForgeError("rpc", `${body.error.message} (rpc ${body.error.code})`);
  }
  return body.result ?? null;
}

// ---------------------------------------------------------------------------
// Org policy switchboard (UI-governs-MCP) — GET|PUT /v1/orgs/{org}/settings
// and /v1/repos/{tenant}/{repo}/settings on the same forge host. Reads are
// open to any admitted caller of the org; WRITES need a verified org-admin
// (the server is fail-closed and its refusals are designed — this module
// surfaces them verbatim). Every key in the readout carries its effective
// value, provenance (org ∘ repo ∘ request-args; org lock wins), lock state,
// and an honest enforced/declared marker: NOTHING SILENT, EVERYTHING
// SWITCHABLE.
// ---------------------------------------------------------------------------

export interface SettingEntry {
  value: Json;
  /** "default" | "org" | "repo" | "org (locked)" | "default (locked)". */
  provenance: string;
  /** Present when an override was refused or shadowed — never silent. */
  note?: string;
  locked: boolean;
  /** Whether a host consumes this value today (vs declares it for the
   *  consumer that reads the same store as it lands). */
  enforced: boolean;
  org_only: boolean;
  type: string; // "boolean" | "string" | "number" | "string[]"
  options?: string[];
  range?: { min: number; max: number };
  description: string;
  updated_by?: string;
  updated_at?: string;
}

export interface SettingsReadout {
  org: string;
  repo?: string;
  scope: "org" | "repo";
  resolution: string;
  keys: Record<string, SettingEntry>;
}

/** One REST round-trip to the switchboard, with the module's designed
 *  ForgeError states (same vocabulary as `rpc`). */
async function settingsRequest(
  path: string,
  method: "GET" | "PUT",
  body?: Json,
): Promise<SettingsReadout> {
  const { apiBase, token } = loadSettings();
  if (!apiBase) {
    throw new ForgeError(
      "unconfigured",
      "No API base configured — point Settings at your Ganforge deployment.",
    );
  }
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetchWithRetry(`${apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ForgeError(
      "unreachable",
      `Could not reach ${apiBase} — is the index host up and CORS-open to this origin?`,
    );
  }
  let parsed: Json = null;
  try {
    parsed = (await res.json()) as Json;
  } catch {
    parsed = null;
  }
  const obj =
    parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as { [k: string]: Json })
      : null;
  if (!res.ok) {
    const detail = typeof obj?.error === "string" ? (obj.error as string) : `HTTP ${res.status}`;
    if (res.status === 401 || res.status === 403) {
      throw new ForgeError("unauthorized", detail, res.status);
    }
    throw new ForgeError("http", detail, res.status);
  }
  if (!obj || typeof obj.keys !== "object") {
    throw new ForgeError("shape", "The host answered, but not with a settings readout.");
  }
  return obj as unknown as SettingsReadout;
}

export function getOrgSettings(org: string): Promise<SettingsReadout> {
  return settingsRequest(`/v1/orgs/${encodeURIComponent(org)}/settings`, "GET");
}

/** Flat `{ key: value }` change set; `null` reverts a key to its default.
 *  The server validates the whole batch against its allowlist — one bad key
 *  rejects everything, nothing is partially applied. */
export function putOrgSettings(
  org: string,
  changes: Record<string, Json>,
): Promise<SettingsReadout> {
  return settingsRequest(`/v1/orgs/${encodeURIComponent(org)}/settings`, "PUT", changes as Json);
}

export function getRepoSettings(ref: RepoRef): Promise<SettingsReadout> {
  return settingsRequest(
    `/v1/repos/${encodeURIComponent(ref.tenant)}/${encodeURIComponent(ref.repo)}/settings`,
    "GET",
  );
}

export function putRepoSettings(
  ref: RepoRef,
  changes: Record<string, Json>,
): Promise<SettingsReadout> {
  return settingsRequest(
    `/v1/repos/${encodeURIComponent(ref.tenant)}/${encodeURIComponent(ref.repo)}/settings`,
    "PUT",
    changes as Json,
  );
}

// ---------------------------------------------------------------------------
// Tool surface — typed wrappers with freshness passthrough
// ---------------------------------------------------------------------------

/** The per-answer freshness envelope (never-block corollary): every answer
 *  names the state it reflects and how long it stays exact. Fields beyond
 *  captured_at are optional at the type level because same-origin answers
 *  are raw casts of the wire JSON; `callTool` always fills all four. */
export interface Freshness {
  captured_at: string;
  head?: string | null;
  versioned_against?: string;
  good_until?: string;
}

export interface ToolDef {
  name: string;
  description: string;
}

export interface ToolAnswer<T> {
  data: T;
  freshness: Freshness | null;
  /** Set when the host truncated to the token budget. */
  truncated: boolean;
  /** The raw text, for panels that show the wire answer. */
  raw: string;
}

export async function toolsList(ref: RepoRef): Promise<ToolDef[]> {
  const result = await rpc(ref, "tools/list");
  const tools =
    result && typeof result === "object" && !Array.isArray(result)
      ? (result as { [k: string]: Json }).tools
      : null;
  if (!Array.isArray(tools)) throw new ForgeError("shape", "tools/list returned no tool array.");
  return tools.flatMap((t): ToolDef[] => {
    if (t && typeof t === "object" && !Array.isArray(t)) {
      const o = t as { [k: string]: Json };
      if (typeof o.name === "string") {
        return [{ name: o.name, description: typeof o.description === "string" ? o.description : "" }];
      }
    }
    return [];
  });
}

const TRUNCATION_MARK = "…truncated: raise max_tokens or narrow the query.";

/** Call one tool and parse its `{ content: [{type:"text", text}] }` answer.
 *  Structured tools return pretty-printed JSON in `text` with an embedded
 *  `freshness` object; prose tools return text with a freshness trailer. */
export async function callTool<T>(ref: RepoRef, name: string, args?: Json): Promise<ToolAnswer<T>> {
  const result = await rpc(ref, "tools/call", { name, arguments: args ?? {} } as Json);
  if (!result || typeof result !== "object" || Array.isArray(result)) {
    throw new ForgeError("shape", `tools/call ${name}: unexpected result shape.`);
  }
  const obj = result as { [k: string]: Json };
  const content = Array.isArray(obj.content) ? obj.content : [];
  const first = content[0];
  const text =
    first && typeof first === "object" && !Array.isArray(first)
      ? String((first as { [k: string]: Json }).text ?? "")
      : "";
  if (obj.isError === true) {
    throw new ForgeError("tool", text.replace(/^error:\s*/, "") || `tool ${name} failed`);
  }
  const truncated = text.endsWith(TRUNCATION_MARK);
  const jsonText = truncated ? text.slice(0, -TRUNCATION_MARK.length) : text;
  let data: T;
  let freshness: Freshness | null = null;
  try {
    const parsed = JSON.parse(jsonText) as { [k: string]: Json };
    if (parsed && typeof parsed === "object" && parsed.freshness) {
      const f = parsed.freshness as { [k: string]: Json };
      freshness = {
        captured_at: typeof f.captured_at === "string" ? f.captured_at : "",
        head: typeof f.head === "string" ? f.head : null,
        versioned_against:
          typeof f.versioned_against === "string" ? f.versioned_against : "working-tree",
        good_until: typeof f.good_until === "string" ? f.good_until : "",
      };
    }
    data = parsed as unknown as T;
  } catch {
    // Prose tool (module_summary / arch_overview): hand back the text.
    data = text as unknown as T;
  }
  return { data, freshness, truncated, raw: text };
}

// ---------------------------------------------------------------------------
// Typed answers for the tools the UI leans on
// ---------------------------------------------------------------------------

export interface RepoMap {
  root: string;
  files: number;
  top_level: Record<string, number>;
  languages: Record<string, number>;
  branches: string[];
  head: string | null;
  /** Optional — populated once the host reports Ed25519 verdicts. */
  head_signature?: SignatureVerdict;
}

/** Verdict shape the host will attach when commit-signature reporting
 *  lands on the wire (Ed25519 trust anchors; `gan keys trust`). Optional
 *  everywhere — the UI renders an honest "unreported" state without it.
 *  The literal set is the union of the repo_map/log_search spelling
 *  (snake_case) and the proposal-flow spelling (hyphenated, per ganvil
 *  docs/proposals/2026-07-08-mcp-api-expansion.md §16), with a `string`
 *  escape hatch until the server actually emits verdicts. */
export interface SignatureVerdict {
  state:
    | "valid_trusted"
    | "valid_unknown"
    | "invalid"
    | "unsigned"
    | "valid-trusted"
    | "valid-untrusted"
    | "unknown-key"
    | "unknown-scheme"
    | string;
  key_id?: string;
  role?: string;
}

export interface CommitHit {
  commit: string;
  summary: string;
  author: string;
  date: string;
  signature?: SignatureVerdict;
}

export interface LogSearchResult {
  matches: number;
  shown: number;
  commits: CommitHit[];
}

export interface OutlineSymbol {
  kind: string;
  name: string;
  lines: string;
  signature: string;
}

export interface OutlineResult {
  path: string;
  language?: string;
  symbols?: OutlineSymbol[];
  note?: string;
}

export type SymbolChangeKind = "added" | "removed" | "signature" | "body" | "moved";

export interface SymbolDiffEntry {
  change: SymbolChangeKind;
  symbol: { kind: string; name: string };
}

export interface SymbolDiffResult {
  old: string;
  new: string;
  files: Record<string, SymbolDiffEntry[]>;
}

export interface SearchProbe {
  query: string;
  /** "covered/total" when a vector tier is live. */
  coverage?: string;
  tier?: string;
  /** Present when no embedding index exists (lexical-only ranking). */
  degraded?: string;
  matches?: number;
}

export const repoMap = (ref: RepoRef) =>
  callTool<RepoMap>(ref, "repo_map", { max_tokens: 600 });

export const logSearch = (ref: RepoRef, limit = 12) =>
  callTool<LogSearchResult>(ref, "log_search", { limit, max_tokens: 1200 });

export const outline = (ref: RepoRef, path: string, at?: string) =>
  callTool<OutlineResult>(ref, "outline", { path, ...(at ? { at } : {}), max_tokens: 1200 });

export const symbolDiff = (ref: RepoRef, oldRev: string, newRev?: string) =>
  callTool<SymbolDiffResult>(ref, "mappings_diff", {
    old: oldRev,
    ...(newRev ? { new: newRev } : {}),
    max_tokens: 1200,
  });

/** Cheap coverage probe: one `search` call, k=1. The answer's tier metadata
 *  says whether a vector index is live and how much of the tree it covers. */
export const searchProbe = (ref: RepoRef) =>
  callTool<SearchProbe>(ref, "search", { query: "index coverage probe", k: 1, max_tokens: 300 });

/** Parse "covered/total" into a ratio in [0,1], or null when unknown. */
export function coverageRatio(coverage: string | undefined): number | null {
  if (!coverage) return null;
  const m = /^(\d+)\s*\/\s*(\d+)$/.exec(coverage);
  if (!m) return null;
  const covered = Number(m[1]);
  const total = Number(m[2]);
  if (!Number.isFinite(covered) || !Number.isFinite(total) || total === 0) return null;
  return Math.max(0, Math.min(1, covered / total));
}

/** "2026-07-09T09:00:12Z" → "09:00:12Z · 2026-07-09" style short stamp. */
export function shortStamp(iso: string): string {
  const t = iso.indexOf("T");
  if (t < 0) return iso;
  return `${iso.slice(t + 1).replace("Z", "")}Z · ${iso.slice(0, t)}`;
}

// ===========================================================================
// Org metrics rollup — the savings dashboard's data contract.
//
// RECONCILED against the real backend (`feat/savings-dashboard-api`,
// `services/search-svc/src/main.rs` `savings_rollup` / `rollup_response`,
// `crates/gc-db/src/platform_event_queries.rs`, `docs/21-platform-events.md`)
// at integration time. The UI track's original draft guessed a
// `GET /v1/orgs/{org}/metrics/rollup` endpoint returning `tier_usage`,
// `dedup`/`compression` savings, and a client/server `disk_fills_avoided`
// split. None of that landed server-side. What's real:
//
//   GET /v1/orgs/{org}/savings?days=N (default 7, max 90)
//   -> 200 { org, window_days, window_start, window_end,
//            read: { ops_per_second, p50_latency_ms, p95_latency_ms, peak_concurrent },
//            write: { ops_per_second, peak_concurrent },
//            by_event_type: { [event_type]: { count, sums: { [field]: number } } } }
//
// There is no `tier_usage` breakdown (lexical/hashed/local_model/hosted
// counts) — `tier` is a free-form string field on each `tier_served` event,
// not summed into a fixed enum server-side; the dashboard falls back to
// reading it out of `by_event_type.tier_served` generically (see
// `tierUsageFromRollup` below). There is no `dedup`/`compression` block —
// `dedup_hit`/`compression_applied` are STUBBED (docs/21-platform-events.md:
// no live call site on any request path yet), so those cards render the
// "not wired yet" empty state until a future wave calls the stub emitters.
// `disk_fills_avoided` is not a client/server split at the wire level —
// both land as the SAME `disk_fill_avoided` event_type (server GC vs. the
// opted-in `gan doctor --fix` client report share one row shape, `trigger`
// distinguishes them); the dashboard derives the split client-side from
// `payload_json.trigger` the rollup doesn't expose per-row (only summed),
// so this wave shows the combined total honestly rather than fabricating a
// client/server breakdown the API cannot back.
//
// `org` is the org UUID (not a tenant name) — `rollup_response`/
// `events_ingest_inner` both require `uuid::Uuid::parse_str`.
// ===========================================================================

/** One operation class's throughput. Reads carry P50/P95 latency; writes
 *  do not (`write_push_sample` is a periodic counter flush with no
 *  per-call duration sample — docs/21-platform-events.md). */
export interface ThroughputStats {
  ops_per_second: number;
  p50_latency_ms?: number;
  p95_latency_ms?: number;
  peak_concurrent: number;
}

/** One `event_type`'s aggregate over the rollup window: how many rows, and
 *  the sum of every numeric `payload_json` field seen across them (e.g.
 *  `tier_served` sums `duration_ms`; `disk_fill_avoided` sums
 *  `bytes_freed`/`entries_removed`). Field presence is best-effort — a
 *  field only appears if at least one row in the window carried it. */
export interface EventTypeAggregate {
  count: number;
  sums: Record<string, number>;
}

export type { OrgMemberLite, PrincipalUsageRow } from "./usageJoin";

export interface OrgSavingsRollup {
  org: string;
  window_days: number;
  window_start: string; // ISO8601
  window_end: string;
  read: ThroughputStats;
  write: ThroughputStats;
  by_event_type: Record<string, EventTypeAggregate>;
  by_principal?: PrincipalUsageRow[];
  by_team?: PrincipalUsageRow[];
  me?: PrincipalUsageRow | null;
}

export { joinUsageWithMembers } from "./usageJoin";

export type MetricsRollupState =
  | { kind: "ok"; data: OrgSavingsRollup }
  | { kind: "unconfigured" }
  | { kind: "gated"; status: number; message: string }
  | { kind: "unreachable"; message: string };

const METRICS_BASE = (import.meta.env.VITE_METRICS_BASE as string) || "/v1";

/** GET the org-wide savings rollup (`GET /v1/orgs/{org}/savings`). Every
 *  branch is a designed state — including "the endpoint isn't there yet"
 *  (404/501 reads as unreachable, not as a crash) and "this org hasn't
 *  opted in to event ingest" (403 from the `telemetry_disk_events` gate
 *  reads as `gated`, same as an entitlement 403) — matching the
 *  degrade-never-fail posture of `probeHammer`. `org` must be the org
 *  UUID; `days` mirrors the server's default (7) and max (90). */
export async function orgMetricsRollup(org: string, days = 7): Promise<MetricsRollupState> {
  if (!org) return { kind: "unconfigured" };
  let res: Response;
  try {
    res = await fetchWithRetry(
      `${METRICS_BASE}/orgs/${encodeURIComponent(org)}/savings?days=${encodeURIComponent(String(days))}`,
      { credentials: "same-origin", headers: gatewayHeaders() },
    );
  } catch (e) {
    return { kind: "unreachable", message: e instanceof Error ? e.message : "network error" };
  }
  if (res.status === 401 || res.status === 403) {
    let message = "not entitled";
    try {
      const body = await res.json();
      message = body?.error ?? message;
    } catch {
      /* keep default */
    }
    return { kind: "gated", status: res.status, message };
  }
  if (!res.ok) {
    return { kind: "unreachable", message: `HTTP ${res.status}` };
  }
  try {
    const data = (await res.json()) as OrgSavingsRollup;
    return { kind: "ok", data };
  } catch {
    return { kind: "unreachable", message: "host answered, but not with the rollup shape" };
  }
}

/** Derive a tier-usage breakdown from `by_event_type.tier_served.sums`.
 *  The server does not group `tier_served` rows by their `tier` string
 *  server-side (it only sums numeric fields generically — `tier` is a
 *  string, not summed), so there is no real per-tier COUNT in the rollup
 *  today. What IS real: total `tier_served` call count. Until the server
 *  adds a per-tier group-by (docs/21-platform-events.md says a future wave
 *  may), the dashboard shows the honest total rather than fabricating a
 *  lexical/hashed/local_model/hosted split the wire shape cannot back. */
export function tierServedCount(rollup: OrgSavingsRollup | null | undefined): number | null {
  return rollup?.by_event_type?.tier_served?.count ?? null;
}

/** `disk_fill_avoided` combined total (client `gan doctor --fix` reports +
 *  server GC reclaims share one event_type — see module docs above). */
export function diskFillAvoided(
  rollup: OrgSavingsRollup | null | undefined,
): { count: number; bytesFreed: number } | null {
  const agg = rollup?.by_event_type?.disk_fill_avoided;
  if (!agg) return null;
  return { count: agg.count, bytesFreed: agg.sums.bytes_freed ?? 0 };
}

// ===========================================================================
// Same-origin gateway surface — the hosted-index tier ("hammer server",
// POST /v1/index/search) and the remote MCP host (POST /mcp/{tenant}/{repo})
// as proxied by the app's own gateway. Cookie auth rides same-origin
// fetches; the session bearer (once token auth lands) is read from
// lib/api.ts so it is wired in exactly one place.
//
// Server contracts (services/search-svc/src/main.rs + ganvil-mcp):
// - /v1/index/search: `{ query, k, at? }` ->
//     200 `{ hits, model, indexed?, coverage?, at, captured_at }`
//     200 `{ warming: true, coverage, model, at, captured_at }`  (<20% built)
//     401/403 `{ error }` (auth / hosted-index entitlement), 5xx `{ error }`.
// - Tool-level failures come back as `isError: true` with a prose message;
//   we classify the known messages into designed UI states rather than
//   treating them as crashes (the server's own posture: degrade, don't fail).
// ===========================================================================

const INDEX_BASE = (import.meta.env.VITE_INDEX_BASE as string) || "/v1";
const MCP_BASE = (import.meta.env.VITE_MCP_BASE as string) || "/mcp";

function gatewayHeaders(): HeadersInit {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  const bearer = getBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

// ---------------------------------------------------------------------------
// Hosted index (REST): coverage probe + tier detection
// ---------------------------------------------------------------------------

/** One ranked hit. Vector hits carry `score` (cosine, 3 decimals); lexical
 *  hits carry symbol/line detail without a score. */
export interface SearchHit {
  type: "symbol" | "file" | "line";
  path: string;
  name?: string;
  kind?: string;
  lines?: string;
  line?: number;
  preview?: string;
  score?: number;
}

export interface IndexAnswer {
  hits?: SearchHit[];
  warming?: boolean;
  coverage?: string; // "covered/total"
  model?: string;
  indexed?: number;
  at?: string; // head OID the answer is versioned against
  captured_at?: string;
}

/** What the live probe of the hammer tier concluded. Every branch is a
 *  designed state — including the failures. */
export type HammerState =
  | { kind: "serving"; answer: IndexAnswer }
  | { kind: "warming"; answer: IndexAnswer }
  | { kind: "gated"; status: number; message: string }
  | { kind: "misconfigured"; message: string }
  | { kind: "unreachable"; message: string };

/** Parse "covered/total" into numbers (0/0 when absent or malformed). */
export function parseCoverage(cov: string | undefined): {
  covered: number;
  total: number;
  pct: number;
} {
  const m = /^(\d+)\s*\/\s*(\d+)$/.exec(cov ?? "");
  if (!m) return { covered: 0, total: 0, pct: 0 };
  const covered = Number(m[1]);
  const total = Number(m[2]);
  return { covered, total, pct: total > 0 ? Math.round((covered / total) * 100) : 0 };
}

/** One cheap round-trip to `/v1/index/search` doubling as the coverage /
 *  tier probe: the response carries coverage, model, head and capture time
 *  whether it is serving or still warming. */
export async function probeHammer(query = "repository shape", k = 1): Promise<HammerState> {
  let res: Response;
  try {
    res = await fetchWithRetry(`${INDEX_BASE}/index/search`, {
      method: "POST",
      headers: gatewayHeaders(),
      body: JSON.stringify({ query, k }),
    });
  } catch (e) {
    return {
      kind: "unreachable",
      message: e instanceof Error ? e.message : "network error",
    };
  }
  const text = await res.text();
  let body: (IndexAnswer & { error?: string }) | undefined;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  if (res.status === 401 || res.status === 403) {
    return { kind: "gated", status: res.status, message: body?.error ?? "not entitled" };
  }
  if (res.status === 503) {
    return { kind: "misconfigured", message: body?.error ?? "index host not configured" };
  }
  if (!res.ok) {
    return { kind: "unreachable", message: body?.error ?? `HTTP ${res.status}` };
  }
  if (!body || (body.hits === undefined && !body.warming)) {
    // 200 that isn't the index protocol (e.g. an SPA fallback page).
    return { kind: "unreachable", message: "host answered, but not with the index protocol" };
  }
  if (body.warming) return { kind: "warming", answer: body };
  return { kind: "serving", answer: body };
}

// ---------------------------------------------------------------------------
// Same-origin MCP JSON-RPC (tools/call) — designed-state results, no throws
// ---------------------------------------------------------------------------

/** Designed error states, classified from the server's prose. */
export type McpErrorKind =
  | "auth" // 401/403 or JSON-RPC auth error: sign in / wrong tenant
  | "unknown-tool" // this host's MCP surface doesn't expose the tool (yet)
  | "signed-only" // server policy: proposals must be Ed25519-signed + anchored
  | "stale" // staleness guard tripped: base moved under the proposal
  | "already-resolved" // proposal is already accepted/rejected
  | "transport" // network / non-JSON / 5xx
  | "tool"; // any other tool-level error message

export type McpResult<T> =
  | { ok: true; data: T; raw: string }
  | { ok: false; kind: McpErrorKind; message: string };

function classify(message: string): McpErrorKind {
  const m = message.toLowerCase();
  if (m.includes("unknown tool")) return "unknown-tool";
  if (m.includes("requires signed proposals") || m.includes("not a trust anchor"))
    return "signed-only";
  if (m.includes("is stale")) return "stale";
  if (m.includes("is already accepted") || m.includes("is already rejected"))
    return "already-resolved";
  return "tool";
}

/** Call one MCP tool on the per-tenant route. The result's `content[0].text`
 *  is parsed as JSON when possible; plain prose comes back as `{ text }`. */
export async function mcpToolCall<T>(
  tenant: string,
  repo: string,
  tool: string,
  args: Record<string, unknown> = {},
): Promise<McpResult<T>> {
  const url = `${MCP_BASE}/${encodeURIComponent(tenant)}/${encodeURIComponent(repo)}`;
  let res: Response;
  try {
    res = await fetchWithRetry(url, {
      method: "POST",
      headers: gatewayHeaders(),
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: ++rpcId,
        method: "tools/call",
        params: { name: tool, arguments: args },
      }),
    });
  } catch (e) {
    return {
      ok: false,
      kind: "transport",
      message: e instanceof Error ? e.message : "network error",
    };
  }
  let body: {
    error?: { code?: number; message?: string };
    result?: { isError?: boolean; content?: Array<{ type: string; text?: string }> };
  };
  try {
    body = await res.json();
  } catch {
    return { ok: false, kind: "transport", message: `HTTP ${res.status}: not JSON-RPC` };
  }
  if (body.error) {
    const msg = body.error.message ?? "request rejected";
    const authy =
      res.status === 401 ||
      res.status === 403 ||
      /auth|bearer|token|tenant|forbidden|unauthorized/i.test(msg);
    return { ok: false, kind: authy ? "auth" : "transport", message: msg };
  }
  const text = body.result?.content?.[0]?.text ?? "";
  if (body.result?.isError) {
    // Tool errors read "error: <reason>" — strip the prefix for display.
    const message = text.replace(/^error:\s*/i, "");
    return { ok: false, kind: classify(message), message };
  }
  try {
    return { ok: true, data: JSON.parse(text) as T, raw: text };
  } catch {
    return { ok: true, data: { text } as unknown as T, raw: text };
  }
}

// ---------------------------------------------------------------------------
// search tool (same-origin)
// ---------------------------------------------------------------------------

/** The `search` tool answer. Vector tiers carry `tier`/`model`/`coverage`/
 *  `tree_state`; the lexical fallback labels itself via `degraded`. */
export interface SearchAnswer {
  query: string;
  hits: SearchHit[];
  matches?: number;
  shown?: number;
  tier?: string; // e.g. "local-vector (npu/gpu)" | "hosted-index (hybrid vector)"
  degraded?: string; // "no-embeddings (lexical+symbol ranking only)"
  model?: string;
  device?: string;
  coverage?: string;
  tree_state?: string;
  indexed?: number;
  at?: string;
  captured_at?: string;
  freshness?: Freshness;
}

export function mcpSearch(
  tenant: string,
  repo: string,
  query: string,
  k = 20,
): Promise<McpResult<SearchAnswer>> {
  return mcpToolCall<SearchAnswer>(tenant, repo, "search", { query, k });
}

// ---------------------------------------------------------------------------
// Proposals flow
// ---------------------------------------------------------------------------
//
// Phase-1 hosts expose only the write-shaped `propose` tool remotely; the
// review verbs below (`proposal_list` / `proposal_show` / `proposal_accept`
// / `proposal_reject`, mirroring the `gan proposal` CLI) are the queue's
// remote surface as specced in ganvil docs/proposals/2026-07-08-mcp-api-
// expansion.md §16. Until a host serves them it answers "unknown tool" —
// which the UI renders as a designed "review locally" state, not a failure.

export interface ProposalChange {
  path: string;
  /** base/new blob OIDs; null base = file added, null new = file deleted. */
  base?: string | null;
  new?: string | null;
}

export interface ProposalSummary {
  id: string;
  title: string;
  status: "open" | "accepted" | "rejected" | string;
  author: string;
  files?: number;
}

export interface ProposalDetail extends ProposalSummary {
  rationale: string;
  changes: ProposalChange[];
  signature?: SignatureVerdict;
  /** Staleness guard: present when the host pre-checked bases against the
   *  index. `stale_paths` names what moved. */
  stale?: boolean;
  stale_paths?: string[];
  freshness?: Freshness;
}

export function listProposals(
  tenant: string,
  repo: string,
): Promise<McpResult<{ proposals: ProposalSummary[] }>> {
  return mcpToolCall(tenant, repo, "proposal_list", {});
}

export function showProposal(
  tenant: string,
  repo: string,
  id: string,
): Promise<McpResult<ProposalDetail>> {
  return mcpToolCall(tenant, repo, "proposal_show", { id });
}

export function acceptProposal(
  tenant: string,
  repo: string,
  id: string,
): Promise<McpResult<{ id: string; status: string }>> {
  return mcpToolCall(tenant, repo, "proposal_accept", { id });
}

export function rejectProposal(
  tenant: string,
  repo: string,
  id: string,
): Promise<McpResult<{ id: string; status: string }>> {
  return mcpToolCall(tenant, repo, "proposal_reject", { id });
}

/** Change kind from the base/new OID pair (mirror of the engine's rule:
 *  content=null deletes; no base = new file). */
export function changeOp(c: ProposalChange): "add" | "delete" | "modify" {
  if (!c.base) return "add";
  if (!c.new) return "delete";
  return "modify";
}

// ---------------------------------------------------------------------------
// Provenance — "who wrote this, and can I trust it" as one narrative answer.
// ---------------------------------------------------------------------------
//
// ASSUMPTION (for the integrator to reconcile): ganvil-mcp does not yet
// expose a tool literally named `provenance` — the closest landed primitive
// is `blame_symbol` (symbol + optional path -> newest-first commits that
// changed it; crates/ganvil-mcp/src/tools.rs). This wrapper calls a
// `provenance` tool first (path + optional line/symbol, per this page's
// spec) and, on "unknown tool", falls back to `blame_symbol` and synthesizes
// the narrative client-side so the page works against today's hosts. When a
// real `provenance` tool lands with this shape server-side, the fallback
// branch below simply stops firing — no page change required.

export interface ProvenanceEvent {
  commit: string;
  summary: string;
  author: string;
  date: string;
  signature?: SignatureVerdict;
}

export interface ProvenanceResult {
  path: string;
  symbol?: string;
  line?: number;
  /** One-sentence plain-language answer, shown prominently at the top. */
  narrative: string;
  /** Newest-first provenance events for the path/symbol. */
  history: ProvenanceEvent[];
  /** Signature verdict of the most recent touching commit — the headline
   *  badge. Undefined (not "unsigned") when the host doesn't report
   *  verdicts at all; `SigChip` renders that as SIG UNREPORTED. */
  latest_signature?: SignatureVerdict;
  /** Set when the host has an open proposal touching this path/symbol —
   *  the provenance page reuses the Proposals visual language to surface
   *  it inline rather than sending the reader away. */
  open_proposal?: ProposalSummary;
  freshness?: Freshness;
}

function narrateFromBlame(
  path: string,
  symbol: string | undefined,
  blame: { symbol: string; path: string; changes: ProvenanceEvent[] },
): ProvenanceResult {
  const [latest, ...rest] = blame.changes;
  const narrative = latest
    ? `${blame.symbol} was last changed in ${latest.commit} by ${latest.author} (${latest.date})` +
      (rest.length > 0 ? `, with ${rest.length} earlier revision${rest.length === 1 ? "" : "s"}.` : ".")
    : `No recorded changes to ${blame.symbol} in ${path}.`;
  return {
    path,
    symbol: symbol ?? blame.symbol,
    narrative,
    history: blame.changes,
    latest_signature: latest?.signature,
  };
}

/** path + optional line/symbol -> the provenance narrative. Line-only
 *  lookups (no symbol name given) are out of reach of `blame_symbol`
 *  (which indexes by symbol, not line number) until a dedicated tool
 *  lands server-side; that case surfaces as a designed `tool` error
 *  asking for a symbol name, not a crash. */
export async function provenance(
  tenant: string,
  repo: string,
  path: string,
  opts: { line?: number; symbol?: string } = {},
): Promise<McpResult<ProvenanceResult>> {
  const direct = await mcpToolCall<ProvenanceResult>(tenant, repo, "provenance", {
    path,
    ...(opts.line !== undefined ? { line: opts.line } : {}),
    ...(opts.symbol ? { symbol: opts.symbol } : {}),
  });
  if (direct.ok || direct.kind !== "unknown-tool") return direct;

  // Fallback: today's hosts only speak blame_symbol, which needs a symbol
  // name. A line-only query can't be resolved client-side — say so plainly.
  if (!opts.symbol) {
    return {
      ok: false,
      kind: "tool",
      message:
        "This host does not expose a provenance tool yet, and its blame_symbol " +
        "fallback needs a symbol name, not just a line number. Add a symbol to search by.",
    };
  }
  const blamed = await mcpToolCall<{ symbol: string; path: string; changes: ProvenanceEvent[] }>(
    tenant,
    repo,
    "blame_symbol",
    { symbol: opts.symbol, path },
  );
  if (!blamed.ok) return blamed;
  return { ok: true, data: narrateFromBlame(path, opts.symbol, blamed.data), raw: blamed.raw };
}
